export type CopyOutcome = 'copied' | 'unverified' | 'failed';

/**
 * Copies text from inside the pod, distinguishing a PROVEN copy from an unprovable one.
 *
 * The pod runs in a cross-origin PSA iframe, where the async Clipboard API is denied by the
 * embedder's Permissions Policy — the page cannot grant itself that permission. So the
 * `execCommand` path is the one that actually runs, and it reports only that the command was
 * accepted, never that the clipboard received anything.
 *
 * Hence three outcomes rather than two. **`unverified` is not success**: telling someone "Copied"
 * when the clipboard may be empty sends them to paste nothing, and they discover it somewhere the
 * mistake costs more than a second attempt would have. Callers are expected to offer the value for
 * manual selection on `unverified` and `failed` alike.
 *
 * Extracted from ScheduleAssist, which solved this first, rather than written twice — two
 * clipboard implementations in one pod is how one of them rots. Its one local dependency, waiting
 * for a modal to close before the textarea can take focus, is injected as `beforeFallback` so this
 * does not reach back into either feature.
 */
export const copyTextToClipboard = async (
  text: string,
  options?: { beforeFallback?: () => Promise<void> },
): Promise<CopyOutcome> => {
  try {
    await navigator.clipboard.writeText(text);
    return 'copied';
  } catch {
    if (options?.beforeFallback) {
      await options.beforeFallback();
    }

    const ta = document.createElement('textarea');
    ta.value = text;
    ta.setAttribute('readonly', 'readonly');
    ta.style.position = 'fixed';
    ta.style.top = '0';
    ta.style.left = '-9999px';
    document.body.appendChild(ta);
    ta.focus();
    ta.select();
    ta.setSelectionRange(0, text.length);

    let ran = false;
    try {
      ran = document.execCommand('copy');
    } catch {
      ran = false;
    }

    document.body.removeChild(ta);
    return ran ? 'unverified' : 'failed';
  }
};

/** Whether the caller must show the value so the user can select it by hand. */
export const needsManualCopy = (outcome: CopyOutcome): boolean => outcome !== 'copied';
